/**
 * The demo's controlled vocabularies — every word a mock screen stores or filters by, in one place.
 *
 * **Where they come from.** The values are the client's Salesforce spellings, as the brief for the
 * two portals lists them (Quality rating and its exclusion reasons, Travel, the ranking families,
 * the disclosure counts, the internal fields), and the rules on them are PRS v1.3 "Shook Pass":
 * §9.1 for Travel, §9.2 for the exclusion reasons, §9.3 for the reasons one survey type adds,
 * §7.7.1 for the eligibility statuses, §9.4 for the contender report's fields. Where the two spell
 * a reason differently the stored value is Salesforce's.
 *
 * None of this is survey-api's: no route stores a Quality rating, an eligibility or a placement
 * yet. It is here so that the day one does, the words it has to accept are already written down
 * and already shown to the people who use them — and so no mock screen invents its own.
 */

/* ------------------------------------------------------------------------------------------------
 * Quality rating — one Salesforce field that carries either a score or the reason a record is out.
 * ---------------------------------------------------------------------------------------------- */

export type QualityScore = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

export const QUALITY_SCORES: readonly QualityScore[] = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1];

/** `10 - High Quality` … `1 - Low Quality`, as the field stores them; the middle is bare. */
export function qualityLabel(score: QualityScore): string {
  if (score === 10) return '10 - High Quality';
  if (score === 1) return '1 - Low Quality';
  return String(score);
}

export interface IExclusionReason {
  /** As Salesforce stores it. */
  value: string;
  /** Survey types it applies to; absent for every type. */
  surveys?: readonly SurveyType[];
}

/** The exclusion reasons the Quality rating field carries, in the brief's order and grouping. */
export const EXCLUSION_REASONS: readonly IExclusionReason[] = [
  { value: 'Left Firm' },
  { value: 'Retired' },
  { value: 'Deceased' },
  { value: 'Unresponsive OR no DD' },
  { value: 'Did not provide updates' },
  { value: 'Needs to be reviewed' },
  { value: 'U4 issues' },
  { value: 'pending U4' },
  { value: 'Firm said do not consider' },
  { value: "Advisor doesn't want to be considered for ANY list" },
  { value: "Advisor doesn't want to be considered for this ranking" },
  { value: 'Not an FA / not client-facing' },
  { value: 'Too Institutional' },
  { value: 'Better fit for RIA' },
  { value: 'Claiming 100%' },
  { value: 'Undeliverable Email Address' },
  { value: 'Unsubscribed' },
  { value: 'Do Not Contact' },
  { value: 'Rude, Hung-up, or Uninterested' },
  // §9.2 carries one reason the brief's list does not; spelled as the PRS spells it.
  { value: 'Too many partners on ranking' }
];

/** PRS §9.3: the reasons one survey type adds. */
export const SURVEY_EXCLUSION_REASONS: readonly IExclusionReason[] = [
  { value: 'Duplicate', surveys: ['TEAMS'] },
  { value: 'Team no longer exists', surveys: ['TEAMS'] },
  { value: 'Incomplete survey (do not carry forward)', surveys: ['TEAMS'] },
  { value: 'Custodian said do not consider', surveys: ['RIA_FIRM'] },
  { value: 'Corporate RIA', surveys: ['RIA_FIRM'] },
  { value: 'Should be TEAMS', surveys: ['RIA_FIRM'] },
  { value: 'Not an FSP', surveys: ['FSP'] },
  { value: 'Inactive license', surveys: ['FSP'] }
];

/** What one Quality rating holds: a score, or a reason. Never both — it is one field. */
export type QualityRating = { score: QualityScore } | { reason: string };

export function qualityRatingLabel(rating: Nullable<QualityRating>): string {
  if (!rating) return '—';
  return 'score' in rating ? qualityLabel(rating.score) : rating.reason;
}

/* ------------------------------------------------------------------------------------------------
 * Travel (§9.1) and eligibility (§7.7.1)
 * ---------------------------------------------------------------------------------------------- */

/** `1` eligible; `NO` not eligible; `2` / `3` eligible with a check, the quality score kept. */
export type Travel = '1' | '2' | '3' | 'NO';

export type Eligibility = 'Eligible' | 'Not eligible' | 'Under review' | 'Not yet reviewed';

/* ------------------------------------------------------------------------------------------------
 * Survey types, ranking families, disclosures
 * ---------------------------------------------------------------------------------------------- */

/** survey-api's survey codes — the four it publishes. */
export type SurveyType = 'FA' | 'TEAMS' | 'FSP' | 'RIA_FIRM';

export const SURVEY_TYPE_LABEL: Record<SurveyType, string> = {
  FA: 'FA',
  TEAMS: 'Team',
  FSP: 'FSP',
  RIA_FIRM: 'RIA'
};

export type RankingFamily = 'BIS' | 'TW' | 'NG' | 'TOP_TEAMS' | 'FSP_250' | 'RIA';

export const RANKING_FAMILY_LABEL: Record<RankingFamily, string> = {
  BIS: 'Best-in-State (BIS)',
  TW: 'Top Women (TW)',
  NG: 'Next-Gen (NG)',
  TOP_TEAMS: 'Top Teams',
  FSP_250: 'FSP Top 250',
  RIA: 'RIA'
};

/** The survey a family is drawn from — which list of records it ranks. */
export const RANKING_FAMILY_SURVEY: Record<RankingFamily, SurveyType> = {
  BIS: 'FA',
  TW: 'FA',
  NG: 'FA',
  TOP_TEAMS: 'TEAMS',
  FSP_250: 'FSP',
  RIA: 'RIA_FIRM'
};

